import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Tag, Modal, Form, Input, Select, DatePicker, message, Popconfirm } from 'antd';
import { EyeOutlined, DeleteOutlined, SendOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { fetchMessages, markMessageAsRead, deleteMessage, setPagination } from '../../features/message/messageSlice';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

const { Option } = Select;
const { TextArea } = Input;

const MessageList = () => {
  const dispatch = useDispatch();
  const { messages, pagination, isLoading, error } = useSelector(state => state.message);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewMessage, setPreviewMessage] = useState(null);
  const [filters, setFilters] = useState({});

  useEffect(() => {
    dispatch(fetchMessages({ ...filters, ...pagination }));
  }, [dispatch, filters, pagination.current, pagination.pageSize]);

  const columns = [
    {
      title: 'ID',
      dataIndex: 'message_id',
      key: 'message_id',
      width: 80,
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: '类型',
      dataIndex: 'message_type',
      key: 'message_type',
      width: 120,
      render: (messageType, record) => {
        const typeMap = {
          'reward': { text: '奖励通知', color: 'blue' },
          'logistics': { text: '物流通知', color: 'green' },
          'fund': { text: '资金通知', color: 'orange' },
          'security': { text: '安全通知', color: 'red' },
          'operation': { text: '运营通知', color: 'purple' },
        };
        const displayName = record.messageTypeName || typeMap[messageType]?.text || messageType;
        const color = typeMap[messageType]?.color || 'default';
        return <Tag color={color}>{displayName}</Tag>;
      },
    },
    {
      title: '接收人',
      key: 'recipient',
      width: 150,
      render: (_, record) => {
        if (record.real_name) {
          return <span>{record.real_name} ({record.phone})</span>;
        } else if (record.phone) {
          return <span>{record.phone}</span>;
        } else if (record.user_id) {
          return <span>用户ID: {record.user_id}</span>;
        }
        return <span>-</span>;
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const statusMap = {
          'unread': { text: '未读', color: 'orange' },
          'read': { text: '已读', color: 'green' },
        };
        const config = statusMap[status] || { text: status, color: 'default' };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '发送时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (createdAt) => createdAt ? dayjs.utc(createdAt).local().format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handlePreview(record)}
          >
            查看
          </Button>
          {record.status === 'unread' && (
            <Button
              type="link"
              onClick={() => handleMarkAsRead(record.message_id)}
            >
              标记已读
            </Button>
          )}
          <Popconfirm
            title="确定要删除这条消息吗？"
            onConfirm={() => handleDelete(record.message_id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handlePreview = (message) => {
    setPreviewMessage(message);
    setPreviewVisible(true);
  };

  const handleMarkAsRead = async (messageId) => {
    try {
      await dispatch(markMessageAsRead(messageId)).unwrap();
      message.success('标记已读成功');
    } catch (error) {
      message.error(error || '标记已读失败');
    }
  };

  const handleDelete = async (messageId) => {
    try {
      await dispatch(deleteMessage(messageId)).unwrap();
      message.success('删除成功');
    } catch (error) {
      message.error(error || '删除失败');
    }
  };

  const handleTableChange = (pagination) => {
    dispatch(setPagination({
      current: pagination.current,
      pageSize: pagination.pageSize,
    }));
  };

  const handleSearch = (values) => {
    const filters = { ...values };
    // 处理日期范围
    if (values.dateRange && values.dateRange.length === 2) {
      filters.startDate = dayjs(values.dateRange[0]).format('YYYY-MM-DD');
      filters.endDate = dayjs(values.dateRange[1]).format('YYYY-MM-DD');
      delete filters.dateRange;
    }
    setFilters(filters);
  };

  const handleReset = () => {
    setFilters({});
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Form
          layout="inline"
          onFinish={handleSearch}
          initialValues={filters}
        >
          <Form.Item name="messageType" label="消息类型">
            <Select placeholder="选择消息类型" allowClear style={{ width: 150 }}>
              <Option value="reward">奖励通知</Option>
              <Option value="logistics">物流通知</Option>
              <Option value="fund">资金通知</Option>
              <Option value="security">安全通知</Option>
              <Option value="operation">运营通知</Option>
            </Select>
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select placeholder="选择状态" allowClear style={{ width: 150 }}>
              <Option value="unread">未读</Option>
              <Option value="read">已读</Option>
            </Select>
          </Form.Item>
          <Form.Item name="dateRange" label="发送时间">
            <DatePicker.RangePicker />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                搜索
              </Button>
              <Button onClick={handleReset}>
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </div>

      <Table
        columns={columns}
        dataSource={messages}
        rowKey="message_id"
        loading={isLoading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条记录`,
        }}
        onChange={handleTableChange}
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
        }}
      />

      <Modal
        title="消息详情"
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={[
          <Button key="close" onClick={() => setPreviewVisible(false)}>
            关闭
          </Button>,
        ]}
        width={600}
      >
        {previewMessage && (
          <div>
            <p><strong>标题：</strong>{previewMessage.title}</p>
            <p><strong>类型：</strong>{previewMessage.messageTypeName || previewMessage.message_type}</p>
            <p><strong>接收人：</strong>
              {previewMessage.real_name ? `${previewMessage.real_name} (${previewMessage.phone})` : 
               previewMessage.phone || `用户ID: ${previewMessage.user_id}` || '-'}
            </p>
            <p><strong>状态：</strong>{previewMessage.status === 'read' ? '已读' : '未读'}</p>
            <p><strong>发送时间：</strong>{previewMessage.created_at ? dayjs.utc(previewMessage.created_at).local().format('YYYY-MM-DD HH:mm:ss') : '-'}</p>
            {previewMessage.read_at && (
              <p><strong>阅读时间：</strong>{dayjs.utc(previewMessage.read_at).local().format('YYYY-MM-DD HH:mm:ss')}</p>
            )}
            <p><strong>内容：</strong></p>
            <div 
              style={{ 
                border: '1px solid #d9d9d9', 
                padding: '12px', 
                borderRadius: '6px',
                backgroundColor: '#fafafa',
                maxHeight: '300px',
                overflow: 'auto'
              }}
              dangerouslySetInnerHTML={{ __html: previewMessage.content }}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MessageList;
